import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API_URL } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Upload, Loader2, CheckCircle2, AlertTriangle, XCircle, Eye, ChevronDown, ChevronUp, Sparkles, ArrowRight, Ban, Check, FileText, RefreshCw } from 'lucide-react';

function fmt(v) { return v != null ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }).format(v) : '—'; }

const REF_LABELS = { achat_vehicule: 'Achat véhicule', vente_vehicule: 'Vente véhicule', frais_vehicule: 'Frais véhicule', charge: 'Charge', frais_mutualise: 'Frais mutualisé' };

const STATUS_LABELS = {
    auto: { label: 'Rapproché', color: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
    probable: { label: 'Probable', color: 'bg-amber-50 text-amber-700 border-amber-200', icon: AlertTriangle },
    valide: { label: 'Validé', color: 'bg-blue-50 text-blue-700 border-blue-200', icon: Check },
    non_rapproche: { label: 'Non rapproché', color: 'bg-gray-50 text-gray-500 border-gray-200', icon: XCircle },
    resolu: { label: 'Résolu', color: 'bg-indigo-50 text-indigo-600 border-indigo-200', icon: Check },
};

export default function OptimisationPage() {
    const { user } = useAuth();
    const [stats, setStats] = useState(null);
    const [operations, setOperations] = useState([]);
    const [totalOps, setTotalOps] = useState(0);
    const [anomalies, setAnomalies] = useState([]);
    const [imports, setImports] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [analyzing, setAnalyzing] = useState(false);
    const [filter, setFilter] = useState('');
    const [mois, setMois] = useState('');
    const [expanded, setExpanded] = useState({});
    const fileRef = useRef(null);
    const [agencies, setAgencies] = useState([]);
    const [selectedAgency, setSelectedAgency] = useState('');
    const [rapport, setRapport] = useState('');
    const [rapportLoading, setRapportLoading] = useState(false);
    const [agenciesReady, setAgenciesReady] = useState(false);

    useEffect(() => {
        axios.get(`${API_URL}/api/agencies`, { withCredentials: true }).then(r => {
            const list = r.data || [];
            setAgencies(list);
            const mine = list.find(a => a.id === user?.agency_id);
            if (list.length >= 1) setSelectedAgency((mine || list[0]).id);
        }).catch(() => {}).finally(() => setAgenciesReady(true));
    }, [user?.agency_id]);

    const qs = useCallback((extra = {}) => {
        const p = new URLSearchParams();
        if (selectedAgency) p.set('agency_id', selectedAgency);
        Object.entries(extra).forEach(([k, v]) => { if (v) p.set(k, v); });
        const str = p.toString();
        return str ? `?${str}` : '';
    }, [selectedAgency]);

    const load = useCallback(async () => {
        if (!agenciesReady) return;
        try {
            const [s, o, a, i] = await Promise.allSettled([
                axios.get(`${API_URL}/api/optim/stats${qs({ mois })}`, { withCredentials: true }),
                axios.get(`${API_URL}/api/optim/operations${qs({ mois, status: filter })}`, { withCredentials: true }),
                axios.get(`${API_URL}/api/optim/anomalies${qs({ mois })}`, { withCredentials: true }),
                axios.get(`${API_URL}/api/optim/imports${qs()}`, { withCredentials: true }),
            ]);
            if (s.status === 'fulfilled') setStats(s.value.data);
            if (o.status === 'fulfilled') { setOperations(o.value.data.operations || []); setTotalOps(o.value.data.total || 0); }
            if (a.status === 'fulfilled') setAnomalies(a.value.data.anomalies || []);
            if (i.status === 'fulfilled') setImports(i.value.data || []);
        } catch {}
    }, [mois, filter, qs, agenciesReady]);

    useEffect(() => { load(); setRapport(''); }, [load]);

    const genererRapport = async () => {
        setRapport(''); setRapportLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/optim/rapport-ia${qs({ mois })}`, { method: 'POST', credentials: 'include' });
            if (!res.ok) { const d = await res.json().catch(() => ({})); toast.error(d.detail || 'Erreur rapport IA'); setRapportLoading(false); return; }
            const reader = res.body.getReader(); const dec = new TextDecoder(); let buf = '';
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buf += dec.decode(value, { stream: true });
                const parts = buf.split('\n\n'); buf = parts.pop();
                for (const part of parts) {
                    const line = part.replace(/^data: /, '');
                    if (line === '[DONE]') continue;
                    try { const ev = JSON.parse(line); if (ev.t) setRapport(r => r + ev.t); if (ev.error) toast.error(ev.error); } catch {}
                }
            }
        } catch { toast.error('Erreur rapport IA'); }
        setRapportLoading(false);
    };

    const handleUpload = async (e) => {
        const files = e.target.files;
        if (!files?.length) return;
        setUploading(true);
        const formData = new FormData();
        for (const f of files) formData.append('files', f);
        try {
            const res = await axios.post(`${API_URL}/api/optim/import${qs()}`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
            const results = res.data.results || [];
            results.forEach(r => {
                if (r.operations > 0) toast.success(`${r.filename} : ${r.operations} opération(s) importée(s)${r.duplicates ? ` (${r.duplicates} doublons ignorés)` : ''}`);
                else if (r.error) toast.error(`${r.filename} : ${r.error}`);
                else toast.warning(`${r.filename} : aucune opération extraite`);
            });
            // Auto-analyze latest import
            const importedIds = results.filter(r => r.import_id).map(r => r.import_id);
            if (importedIds.length > 0) {
                setAnalyzing(true);
                for (const id of importedIds) {
                    try {
                        const ar = await axios.post(`${API_URL}/api/optim/analyze/${id}${qs()}`, {}, { withCredentials: true });
                        toast.success(`Rapprochement : ${ar.data.matched} auto, ${ar.data.probable} probables, ${ar.data.unmatched} non rapprochés`);
                    } catch { toast.error('Erreur analyse'); }
                }
                setAnalyzing(false);
            }
            load();
        } catch (err) { toast.error(err?.response?.data?.detail || 'Erreur import'); }
        setUploading(false);
        if (fileRef.current) fileRef.current.value = '';
    };

    const handleAction = async (opId, action, note) => {
        try {
            await axios.put(`${API_URL}/api/optim/rapprochement/${opId}${qs()}`, { action, note }, { withCredentials: true });
            toast.success({ valider: 'Rapprochement validé', rejeter: 'Rapprochement rejeté', resolu: 'Marqué résolu', annuler: 'Action annulée' }[action]);
            load();
        } catch { toast.error('Erreur'); }
    };

    const toggle = (k) => setExpanded(p => ({ ...p, [k]: !p[k] }));

    const monthOpts = (() => {
        const opts = [{ value: '', label: 'Tous les mois' }];
        const now = new Date();
        for (let i = 0; i < 12; i++) {
            const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
            const v = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
            opts.push({ value: v, label: d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }) });
        }
        return opts;
    })();

    return (
        <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-6" data-testid="optim-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-xl font-semibold text-gray-900 flex items-center gap-2" data-testid="optim-title">
                        <Sparkles className="w-5 h-5 text-[#6366F1]" />Optimisation IA
                    </h1>
                    <p className="text-sm text-gray-400 mt-0.5">Contrôle intelligent des dépenses et rapprochement bancaire</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {agencies.length > 1 && (
                        <Select value={selectedAgency} onValueChange={setSelectedAgency}>
                            <SelectTrigger className="w-72 h-9 text-sm" data-testid="optim-agency-select"><SelectValue placeholder="Sélectionner une agence" /></SelectTrigger>
                            <SelectContent>{agencies.map(a => { const n = (a.name || '').trim(); const dup = agencies.filter(b => (b.name || '').trim() === n).length > 1; return <SelectItem key={a.id} value={a.id}>{n ? (dup ? `${n} (#${a.id.slice(-4)})` : n) : `Agence ${a.id.slice(-4)}`}</SelectItem>; })}</SelectContent>
                        </Select>
                    )}
                    <Select value={mois || 'all'} onValueChange={v => setMois(v === 'all' ? '' : v)}>
                        <SelectTrigger className="w-44 h-9 text-sm" data-testid="optim-month-select"><SelectValue placeholder="Tous les mois" /></SelectTrigger>
                        <SelectContent>{monthOpts.map(m => <SelectItem key={m.value || 'all'} value={m.value || 'all'}>{m.label}</SelectItem>)}</SelectContent>
                    </Select>
                    <input ref={fileRef} type="file" accept=".csv,.pdf" multiple onChange={handleUpload} className="hidden" data-testid="optim-file-input" />
                    <Button onClick={() => fileRef.current?.click()} disabled={uploading || analyzing}
                        className="h-9 px-4 text-sm bg-[#6366F1] hover:bg-[#4F46E5] text-white" data-testid="optim-import-btn">
                        {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : analyzing ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                        {uploading ? 'Import...' : analyzing ? 'Analyse...' : 'Importer un relevé'}
                    </Button>
                </div>
            </div>

            {/* KPIs */}
            {stats?.total_operations !== undefined && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="optim-kpis">
                    <KPI label="Opérations" value={stats.total_operations} />
                    <KPI label="Rapprochées" value={`${String(stats.pct_rapproche).replace('.', ',')} %`} sub={`${stats.rapproche_auto + stats.valide} / ${stats.total_operations}`} />
                    <KPI label="Anomalies" value={anomalies.length} warn={anomalies.length > 0} />
                    <KPI label="Dépenses analysées" value={fmt(stats.total_debits)} />
                </div>
            )}

            {/* Rapport IA */}
            {stats?.total_operations > 0 && (
                <div className="rounded-2xl border border-indigo-100 bg-white p-5" data-testid="optim-rapport-card">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div>
                            <div className="text-sm font-semibold text-gray-900 flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#6366F1]" />Synthèse IA</div>
                            <div className="text-xs text-gray-400 mt-0.5">Explication des chiffres ci-dessus. L'IA ne modifie aucune donnée.</div>
                        </div>
                        <Button onClick={genererRapport} disabled={rapportLoading} variant="outline" className="h-8 text-xs" data-testid="optim-rapport-btn">
                            {rapportLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
                            {rapport ? 'Régénérer' : 'Générer la synthèse'}
                        </Button>
                    </div>
                    {rapport && <div className="mt-4 text-sm text-gray-700 whitespace-pre-wrap leading-relaxed" data-testid="optim-rapport-text">{rapport}</div>}
                </div>
            )}

            {/* Anomalies */}
            {anomalies.length > 0 && (
                <div className="rounded-2xl border border-amber-200 bg-white overflow-hidden">
                    <button onClick={() => toggle('anom')} className="w-full flex items-center justify-between px-5 py-3 bg-amber-50 border-b border-amber-200">
                        <span className="text-sm font-semibold text-amber-800 flex items-center gap-2"><AlertTriangle className="w-4 h-4" />{anomalies.length} anomalie(s) détectée(s)</span>
                        {expanded.anom ? <ChevronUp className="w-4 h-4 text-amber-600" /> : <ChevronDown className="w-4 h-4 text-amber-600" />}
                    </button>
                    {expanded.anom && (
                        <div className="divide-y divide-gray-50">
                            {anomalies.map((a, i) => (
                                <div key={i} className="px-5 py-3 flex items-center justify-between gap-3">
                                    <div className="min-w-0">
                                        <p className="text-[12px] font-medium text-gray-700">{a.message}</p>
                                        <p className="text-[11px] text-gray-400 mt-0.5">{a.date} — {a.libelle}</p>
                                    </div>
                                    <span className="text-[12px] font-mono font-semibold text-red-600 shrink-0">{fmt(a.montant)}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Operations */}
            <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
                <div className="flex items-center justify-between px-5 py-3 bg-gray-50 border-b border-gray-100">
                    <h3 className="text-sm font-semibold text-gray-800">Opérations bancaires <span className="text-gray-400 font-normal">({totalOps})</span></h3>
                    <Select value={filter || 'all'} onValueChange={v => setFilter(v === 'all' ? '' : v)}>
                        <SelectTrigger className="w-40 h-8 text-[11px]" data-testid="optim-status-filter"><SelectValue placeholder="Tous les statuts" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Tous</SelectItem>
                            <SelectItem value="resolu">Résolus</SelectItem>
                            <SelectItem value="auto">Rapprochés</SelectItem>
                            <SelectItem value="probable">Probables</SelectItem>
                            <SelectItem value="non_rapproche">Non rapprochés</SelectItem>
                            <SelectItem value="valide">Validés</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                {operations.length === 0 ? (
                    <div className="px-5 py-10 text-center text-gray-400 text-sm">
                        {stats?.total_operations === 0
                            ? <><Upload className="w-8 h-8 mx-auto mb-3 text-gray-300" /><p>Importez votre premier relevé bancaire pour commencer l'analyse.</p></>
                            : 'Aucune opération pour ce filtre.'}
                    </div>
                ) : (
                    <div className="divide-y divide-gray-50 max-h-[500px] overflow-y-auto">
                        {operations.map(op => {
                            const st = STATUS_LABELS[op.rapprochement_status] || STATUS_LABELS.non_rapproche;
                            const Icon = st.icon;
                            const ref = op.rapprochement_ref;
                            return (
                                <div key={op.id} className="px-5 py-3 hover:bg-gray-50/50 transition-colors" data-testid={`op-${op.id}`}>
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="flex items-start gap-3 min-w-0">
                                            <span className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center border ${st.color}`}>
                                                <Icon className="w-3.5 h-3.5" />
                                            </span>
                                            <div className="min-w-0">
                                                <p className="text-[13px] font-medium text-gray-700 break-words">{op.libelle || '—'}</p>
                                                <p className="text-[11px] text-gray-400">{op.date_operation}</p>
                                                {ref && (
                                                    <span className="inline-block mt-1 text-[11px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 max-w-full truncate" data-testid={`op-ref-${op.id}`}>
                                                        {REF_LABELS[ref.type] || ref.type}
                                                        {(ref.nom || ref.fournisseur || ref.libelle) && ` — ${ref.nom || ref.fournisseur || ref.libelle}`}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex flex-col items-end gap-1.5 shrink-0">
                                            <span className={`text-[13px] font-mono font-semibold ${op.montant < 0 ? 'text-red-600' : 'text-emerald-600'}`}>{fmt(op.montant)}</span>
                                            {(op.rapprochement_status === 'auto' || op.rapprochement_status === 'probable') && (
                                                <div className="flex gap-1">
                                                    <button onClick={() => handleAction(op.id, 'valider')} className="p-2 rounded hover:bg-emerald-50 text-emerald-600" title="Valider" data-testid={`op-validate-${op.id}`}><Check className="w-4 h-4" /></button>
                                                    <button onClick={() => handleAction(op.id, 'rejeter')} className="p-2 rounded hover:bg-red-50 text-red-500" title="Rejeter" data-testid={`op-reject-${op.id}`}><Ban className="w-4 h-4" /></button>
                                                </div>
                                            )}
                                            {op.rapprochement_status === 'non_rapproche' && (
                                                <button onClick={() => handleAction(op.id, 'resolu', 'Marqué comme résolu')} className="text-xs px-2 py-1 text-[#6366F1] hover:underline" data-testid={`op-resolve-${op.id}`}>Résoudre</button>
                                            )}
                                            {(op.rapprochement_status === 'valide' || op.rapprochement_status === 'resolu') && (
                                                <button onClick={() => handleAction(op.id, 'annuler')} className="text-xs px-2 py-1 text-gray-500 hover:underline" data-testid={`op-undo-${op.id}`}>Annuler</button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Import history */}
            {imports.length > 0 && (
                <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
                    <button onClick={() => toggle('imports')} className="w-full flex items-center justify-between px-5 py-3 bg-gray-50 border-b border-gray-100">
                        <span className="text-sm font-semibold text-gray-800 flex items-center gap-2"><FileText className="w-4 h-4 text-gray-500" />Historique imports ({imports.length})</span>
                        {expanded.imports ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                    </button>
                    {expanded.imports && (
                        <div className="divide-y divide-gray-50">
                            {imports.map(imp => (
                                <div key={imp.id} className="px-5 py-2.5 flex items-center justify-between text-[12px]">
                                    <div>
                                        <span className="font-medium text-gray-700">{imp.filename}</span>
                                        <span className="text-gray-400 ml-2">{imp.format?.toUpperCase()}</span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <span className="text-gray-500">{imp.nb_operations} op.</span>
                                        {imp.nb_duplicates > 0 && <span className="text-amber-500">{imp.nb_duplicates} doublons</span>}
                                        <span className="text-gray-400">{imp.created_at?.slice(0, 10)}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            <p className="text-[11px] text-gray-400">Ce module analyse vos relevés bancaires et les croise avec vos données Marge Auto. Aucune modification automatique de vos données financières.</p>
        </div>
    );
}

function KPI({ label, value, sub, warn }) {
    return (
        <div className={`rounded-xl border px-3.5 py-3 ${warn ? 'bg-amber-50 border-amber-200' : 'bg-white border-gray-200'}`}>
            <div className="text-[10px] text-gray-400 uppercase tracking-wider font-medium">{label}</div>
            <div className={`text-lg font-bold mt-0.5 ${warn ? 'text-amber-700' : 'text-gray-900'}`}>{value}</div>
            {sub && <div className="text-[10px] text-gray-400 mt-0.5">{sub}</div>}
        </div>
    );
}
